import React from 'react';

function inline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\((https?:\/\/[^)\s]+)\)|`[^`]+`)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let index = 0;
  while ((match = pattern.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={`${keyPrefix}-b-${index++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (linkMatch) {
        nodes.push(
          <a
            key={`${keyPrefix}-a-${index++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer"
            className="font-bold text-palm underline decoration-gecko/40 underline-offset-2 hover:text-gecko"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith('`')) {
      nodes.push(
        <code key={`${keyPrefix}-c-${index++}`} className="px-1 py-0.5 rounded bg-stone-100 text-[0.9em]">
          {token.slice(1, -1)}
        </code>,
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** A small, safe Markdown renderer for the compendium's headings, lists,
 *  links, bold text and tables. It never uses dangerouslySetInnerHTML. */
const MarkdownLite: React.FC<{ content: string }> = ({ content }) => {
  const lines = String(content || '').replace(/\r/g, '').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    // Table block
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const tableLines: string[] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines
        .filter(row => !/^\s*\|?\s*:?-{2,}/.test(row))
        .map(row => row.trim().replace(/^\||\|$/g, '').split('|').map(cell => cell.trim()));
      if (rows.length) {
        blocks.push(
          <div key={`table-${key++}`} className="overflow-x-auto my-4">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  {rows[0].map((cell, ci) => (
                    <th key={ci} className="text-left font-black text-bark bg-stone-100 px-3 py-2 border border-stone-200">
                      {inline(cell, `th-${key}-${ci}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(1).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="align-top text-stone-600 px-3 py-2 border border-stone-200 leading-relaxed">
                        {inline(cell, `td-${key}-${ri}-${ci}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      continue;
    }

    if (/^###\s/.test(line)) {
      blocks.push(<h4 key={`h4-${key++}`} className="text-sm font-black text-bark mt-5 mb-2">{line.replace(/^###\s*/, '')}</h4>);
      i++;
      continue;
    }
    if (/^##\s/.test(line)) {
      blocks.push(<h3 key={`h3-${key++}`} className="text-base font-black text-bark mt-6 mb-2">{line.replace(/^##\s*/, '')}</h3>);
      i++;
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`ul-${key++}`} className="list-disc pl-5 space-y-1 my-3 text-stone-600">
          {items.map((item, ii) => <li key={ii}>{inline(item, `li-${key}-${ii}`)}</li>)}
        </ul>,
      );
      continue;
    }

    // Paragraph: gather until blank or a structural line.
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*\|/.test(lines[i]) &&
      !/^#{2,3}\s/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i++;
    }
    if (paragraph.length) {
      blocks.push(
        <p key={`p-${key++}`} className="text-sm text-stone-600 leading-relaxed my-2">
          {inline(paragraph.join(' '), `p-${key}`)}
        </p>,
      );
    }
  }

  return <div>{blocks}</div>;
};

export default MarkdownLite;
